import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { action, computed, makeObservable, observable } from 'mobx';
import { Document } from 'types/documentsService';

export interface DocumentContextualProps {
  document?: Document;
  onSubmit: () => void;
  getExistingDocumentTypes: () => string[];
}

interface StaticDataFormFields extends DefaultFormFields {
  docName: FormControl<string>;
  templateLink: FormControl<string>;
  description: FormControl<string>;
}

export abstract class DocumentStaticDataStore {
  private _isLoading = false;
  // On submission callback function
  private readonly _onSubmit;
  protected _getExistingDocumentTypes;

  public form: FormController<StaticDataFormFields>;
  public uploadedFile: AcceptedFiles | null = null;

  get isDataValid(): boolean {
    return this.form.basicFieldsValidationsPassed;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  protected _nameIsUnique(value: string): boolean {
    const existingDocs = this._getExistingDocumentTypes();
    return !existingDocs.find((docType) => docType.toLowerCase() === value.toLowerCase().trim());
  }

  private _setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public setFile(file: AcceptedFiles | null): void {
    this.uploadedFile = file;
  }

  public resetModal(): void {
    this.form.resetForm();
    this.setFile(null);
  }

  public async requestDocumentAction(): Promise<void> {
    this._setIsLoading(true);
    await this._processDocumentAction();
    this.resetModal();
    this._setIsLoading(false);
    this._onSubmit();
  }

  protected abstract _processDocumentAction(): Promise<void>;

  constructor({ document, onSubmit, getExistingDocumentTypes }: DocumentContextualProps) {
    this._onSubmit = onSubmit;
    this._getExistingDocumentTypes = getExistingDocumentTypes;

    this.form = new FormController<StaticDataFormFields>({
      docName: new FormControl(document?.type || '', {
        validations: ['printableCharacters'],
        customValidations: [
          {
            check: this._nameIsUnique.bind(this),
            msg: 'Document of this type already exists.',
          },
        ],
        optional: false,
      }),
      templateLink: new FormControl(document?.templateLink || '', {
        validations: ['validUrl'],
        optional: true,
      }),
      description: new FormControl(document?.description || '', {
        validations: ['printableCharacters'],
        optional: true,
      }),
    });

    makeObservable<DocumentStaticDataStore, '_setIsLoading' | '_isLoading' | '_onSubmit' | '_nameIsUnique'>(this, {
      _isLoading: observable,
      form: observable,
      uploadedFile: observable,

      isDataValid: computed,
      isLoading: computed,

      _setIsLoading: action.bound,
      _onSubmit: action,
      _nameIsUnique: action.bound,
      requestDocumentAction: action.bound,
      resetModal: action.bound,
      setFile: action.bound,
    });
  }
}
